import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { farmerApi, type Buyable, type Offer } from '../lib/farmer'
import { money, num } from '../lib/format'
import { Banner, Empty, Loading, Panel } from '../ui/kit'
import { FarmerHeader } from './FarmerShell'

/**
 * Posting a harvest. Three questions: what, how much, and when.
 *
 * The number the farmer types is an estimate and the screen says so plainly. What they
 * are actually paid comes from the scale when the agent arrives — telling them that
 * here, before they agree, is the difference between a fair deal and an argument.
 */
export function PostHarvest() {
  const nav = useNavigate()
  const [buy, setBuy] = useState<Buyable[]>([])
  const [loading, setLoading] = useState(true)

  const [cropId, setCropId] = useState('')
  const [gradeId, setGradeId] = useState('')
  const [kg, setKg] = useState('')
  const [readyFrom, setReadyFrom] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  })
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [posted, setPosted] = useState<Offer | null>(null)

  useEffect(() => {
    farmerApi
      .get<Buyable[]>('/portal/what-we-buy')
      .then(setBuy)
      .catch(() => setBuy([]))
      .finally(() => setLoading(false))
  }, [])

  const crops = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; localName: string | null }>()
    for (const b of buy) {
      if (!seen.has(b.cropId)) seen.set(b.cropId, { id: b.cropId, name: b.cropName, localName: b.localName })
    }
    return [...seen.values()]
  }, [buy])

  const grades = useMemo(() => buy.filter((b) => b.cropId === cropId), [buy, cropId])
  const chosen = grades.find((g) => g.gradeId === gradeId) ?? grades[0]
  const kgNumber = Number(kg) || 0
  const estimate = chosen ? kgNumber * Number(chosen.pricePerKg) : 0

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const offer = await farmerApi.post<Offer>('/portal/offers', {
        cropId,
        claimedGradeId: chosen?.gradeId ?? null,
        estimatedKg: kgNumber,
        readyFrom,
        notes: notes.trim() || null,
      })
      setPosted(offer)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not post that.')
    } finally {
      setBusy(false)
    }
  }

  if (posted) {
    return (
      <>
        <FarmerHeader title="Posted" back={() => nav('/f')} />
        <div className="field-body">
          <div className="brand-card" style={{ alignItems: 'center', textAlign: 'center' }}>
            <span className="micro">Posted</span>
            <div className="bc-title" style={{ justifyContent: 'center' }}>
              {posted.cropName} · {num(posted.estimatedKg, 0)} kg
            </div>
            <div className="bc-hero num">{money(posted.indicativeValue)}</div>
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--on-brand-dim)' }}>
              at today's price, if it all grades {posted.claimedGradeCode ?? 'as offered'}
            </p>
          </div>

          <Banner tone="info" title="What happens next">
            We will reply and give you a collection day. An agent comes to you, weighs it on a
            certified scale, and you are paid the same day.
          </Banner>

          <button className="btn lg block" onClick={() => nav('/f/harvests')}>See my harvests</button>
          <button
            className="btn ghost block"
            onClick={() => { setPosted(null); setKg(''); setNotes('') }}
          >
            Post another
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <FarmerHeader />
      <div className="field-greet">
        <h1>Post a harvest</h1>
        <p>Tell us what you will have and when. We will come for it.</p>
      </div>

      <div className="field-body">
        {loading && <Panel><Loading rows={3} /></Panel>}

        {!loading && crops.length === 0 && (
          <Empty title="No prices published yet">
            We are not buying at the moment. Check back, or ask your agent.
          </Empty>
        )}

        {crops.length > 0 && (
          <form onSubmit={submit} className="col" style={{ gap: 'var(--sp-7)' }}>
            <div className="field">
              <label htmlFor="crop">What do you have?</label>
              <select
                id="crop"
                className="input"
                style={{ minHeight: 52, fontSize: 'var(--fs-lg)' }}
                value={cropId}
                onChange={(e) => { setCropId(e.target.value); setGradeId('') }}
                required
              >
                <option value="">Choose a crop</option>
                {crops.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.localName ? ` (${c.localName})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {grades.length > 1 && (
              <div className="field">
                <label htmlFor="grade">What quality is it?</label>
                <select
                  id="grade"
                  className="input"
                  style={{ minHeight: 52, fontSize: 'var(--fs-lg)' }}
                  value={chosen?.gradeId ?? ''}
                  onChange={(e) => setGradeId(e.target.value)}
                >
                  {grades.map((g) => (
                    <option key={g.gradeId} value={g.gradeId}>
                      Grade {g.gradeCode} — {money(g.pricePerKg, { currency: false })}/kg
                    </option>
                  ))}
                </select>
                {chosen && <span className="hint">{chosen.specText}</span>}
              </div>
            )}

            <div className="field">
              <label htmlFor="kg">About how many kilos?</label>
              <input
                id="kg"
                className="input n"
                style={{ minHeight: 56, fontSize: 'var(--fs-2xl)', textAlign: 'center' }}
                type="number"
                inputMode="decimal"
                step="1"
                min="1"
                placeholder="0"
                value={kg}
                onChange={(e) => setKg(e.target.value)}
                required
              />
              <span className="hint">
                Your best guess is fine. We weigh it on our scale when we arrive, and that
                weight is what you are paid for.
              </span>
            </div>

            <div className="field">
              <label htmlFor="ready">When will it be ready?</label>
              <input
                id="ready"
                className="input"
                style={{ minHeight: 52, fontSize: 'var(--fs-lg)' }}
                type="date"
                value={readyFrom}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setReadyFrom(e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="notes">Anything we should know? <span className="faint">(optional)</span></label>
              <textarea
                id="notes"
                className="input"
                rows={2}
                placeholder="Picking Thursday morning, ready by 7am"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {chosen && kgNumber > 0 && (
              <div className="price-strip">
                <div className="ps-left">
                  <i>Today's price</i>
                  <b>{money(chosen.pricePerKg, { currency: false })} / kg</b>
                </div>
                <div className="ps-right">
                  <i>About</i>
                  <b>{money(estimate)}</b>
                </div>
              </div>
            )}

            {error && <div className="error-text">{error}</div>}

            <button className="btn lg block" disabled={busy || !cropId || kgNumber <= 0}>
              {busy ? 'Posting…' : 'Post this harvest'}
            </button>

            <p className="hint" style={{ textAlign: 'center' }}>
              This is an offer, not a sale. Prices can move, and the weight that counts is the
              one on the scale on the day.
            </p>
          </form>
        )}
      </div>
    </>
  )
}
